import dayjs from "dayjs";

import { PlayFilledSVG } from "@newsela/angelou";

import { ContentTypeEnum } from "./ContentCardV2.interface";

const secondsInHour = 3600;
const secondsInTenMin = 600;

export const getAssignHref = (
  returnUrl: string,
  contentId: string,
  contentType: string,
) => {
  if (
    contentType === ContentTypeEnum.ARTICLE ||
    contentType === ContentTypeEnum.GENERATION_GENIUS_VIDEO
  ) {
    return `/view/${contentId}`;
  } else {
    return `/assignments/create?return_url=${returnUrl}&createdFrom=${contentId}&contentIds=${contentId}`;
  }
};

const gradeMap = {
  0: "Kindergarten ",
  1: "1st",
  2: "2nd",
  3: "3rd",
  4: "4th",
  5: "5th",
  6: "6th",
  7: "7th",
  8: "8th",
  9: "9th",
  10: "10th",
  11: "11th",
  12: "12th",
};

export const getFormattedGradeLevelLabel = (
  gradeLevels: number[] | undefined,
): string => {
  if (!gradeLevels || gradeLevels.length === 0) return "";

  const sortedGrades = gradeLevels.sort((a, b) => a - b);

  const formatResult = (grade: keyof typeof gradeMap) =>
    gradeMap[grade] || `${grade}th`;
  const lowestGradeValue = sortedGrades[0];
  const highestGradeValue = sortedGrades[sortedGrades.length - 1];
  const lowestGradeIsDifferentFromHighestGrade =
    lowestGradeValue !== highestGradeValue;
  if (lowestGradeIsDifferentFromHighestGrade) {
    return `${formatResult(lowestGradeValue as keyof typeof gradeMap)}-${formatResult(highestGradeValue as keyof typeof gradeMap)}`;
  } else {
    return formatResult(lowestGradeValue as keyof typeof gradeMap);
  }
};

export const getFormattedLexileLevelLabel = (
  lexileLevels: number[] | undefined,
): string => {
  if (!lexileLevels?.length || !lexileLevels[0]) return "";

  const sortedLexiles = lexileLevels.sort((a, b) => a - b);

  if (lexileLevels?.length === 1) return `${sortedLexiles[0] as number}L`;

  const lowestLexile = sortedLexiles[0]?.toString() as string;
  const highestLexile = sortedLexiles[
    sortedLexiles.length - 1
  ]?.toString() as string;

  return lowestLexile && highestLexile
    ? `${lowestLexile}L-${highestLexile}L`
    : "";
};

export const getFormattedDuration = (
  duration: number,
  formatObj = {
    lg: "H:mm:ss",
    md: "mm:ss",
    sm: "m:ss",
  },
) => {
  return duration >= secondsInHour
    ? dayjs.utc(duration * 1000).format(formatObj.lg)
    : duration >= secondsInTenMin
      ? dayjs.utc(duration * 1000).format(formatObj.md)
      : dayjs.utc(duration * 1000).format(formatObj.sm);
};
const ariaFormatObj = {
  lg: "H [hours] mm [minutes] ss [seconds]",
  md: "mm [minutes] ss [seconds]",
  sm: "m [minutes] ss [seconds]",
};

/**
 * returns an object that has a visually appealing
 * date as well as one for screen readers
 */
export const durationFormatted = (duration: number) => ({
  value: getFormattedDuration(duration),
  ariaLabel: getFormattedDuration(duration, ariaFormatObj),
  icon: PlayFilledSVG,
});
